import { apiBuilder } from "@zodios/core";
import { z } from "zod";

const alertItemSchema = z.object({
  field: z.string(),
  message: z.string(),
});

const alertSchema = z.object({
  row_number: z.number(),
  data: z.record(z.unknown()),
  errors: z.object({
    errors: z.array(alertItemSchema),
    warnings: z.array(alertItemSchema),
  }),
});

const alertsArraySchema = z.array(alertSchema);

const uploadLevelAlertsSchema = z.object({
  errors: z.array(alertItemSchema),
  warnings: z.array(alertItemSchema),
});

const alertsFieldSchema = z.union([alertsArraySchema, uploadLevelAlertsSchema]);

const uploadTypeSchema = z.enum(["pricing_plan", "bulk_upload", "price_link_upload"]);

export type UploadType = z.infer<typeof uploadTypeSchema>;

const uploadSchema = z.object({
  id: z.number(),
  created_on: z.string().datetime(),
  updated_on: z.string().datetime(),
  status: z.number(),
  upload_type: uploadTypeSchema,
  name: z.string(),
  file_type: z.literal("text/csv"),
  progress: z.number(),
  org: z.number(),
  created_by: z.number().nullable().optional(),
  alerts: alertsFieldSchema.nullable().optional(),
  warnings: alertsArraySchema.nullable().optional(),
  total_rows: z.number().optional(),
  valid_rows: z.number().optional(),
  warning_rows: z.number().optional(),
  invalid_rows: z.number().optional(),
  committed_rows: z.number().optional(),
  failed_rows: z.number().optional(),
});

export type Upload = z.infer<typeof uploadSchema>;
export type Alert = z.infer<typeof alertSchema>;
export type UploadLevelAlerts = z.infer<typeof uploadLevelAlertsSchema>;

const processUploadResponseSchema = z.object({
  id: z.number(),
  status: z.number(),
  progress: z.number(),
  alerts: alertsFieldSchema.nullable().optional(),
  warnings: alertsArraySchema.nullable().optional(),
});

const uploadFormSchema = z.object({
  url: z.string().url(),
  fields: z.record(z.string()),
});

export const uploadsApiDefinitions = apiBuilder({
  alias: "createUpload",
  method: "post",
  path: "/upload/",
  status: 201,
  parameters: [
    {
      type: "Body",
      name: "body",
      schema: z.object({
        upload_type: uploadTypeSchema,
        file_type: z.literal("text/csv"),
        name: z.string(),
      }),
    },
  ],
  response: uploadSchema,
})
  .addEndpoint({
    alias: "getUpload",
    method: "get",
    path: "/upload/:id/",
    status: 200,
    response: uploadSchema,
  })
  .addEndpoint({
    alias: "getUploadForm",
    method: "get",
    path: "/upload/:id/form/",
    status: 200,
    response: uploadFormSchema,
  })
  .addEndpoint({
    alias: "processUpload",
    method: "post",
    path: "/upload/:id/process/",
    status: 200,
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: z.number(),
      },
      {
        type: "Body",
        name: "body",
        schema: z
          .object({
            marketplace: z.string().optional(),
            async_run: z.boolean().optional(),
            ignoreWarnings: z.boolean().optional(),
            autoProcess: z.boolean().optional(),
            follower_org_id: z.number().optional(),
            additional_details: z.record(z.unknown()).optional(),
          })
          .optional(),
      },
    ],
    response: processUploadResponseSchema,
  })
  .addEndpoint({
    alias: "listPricingPlanUploadRows",
    method: "get",
    path: "/upload/:id/pricing-plan-rows/",
    parameters: [
      { type: "Path", name: "id", schema: z.number() },
      { type: "Query", name: "status", schema: z.string().optional() },
      { type: "Query", name: "limit", schema: z.number().optional() },
      { type: "Query", name: "page", schema: z.number().optional() },
    ],
    response: z.object({
      count: z.number(),
      next: z.string().nullable(),
      previous: z.string().nullable(),
      results: z.array(
        z.object({
          id: z.number(),
          row_number: z.number(),
          status: z.string(),
          raw_payload: z.record(z.unknown()),
          errors: z.array(alertItemSchema),
          warnings: z.array(alertItemSchema),
          target_plan: z.number().nullable(),
          error_message: z.string().nullable(),
        }),
      ),
    }),
  })
  .addEndpoint({
    alias: "downloadPricingPlanErrorCsv",
    method: "get",
    path: "/upload/:id/pricing-plan-error-csv/",
    parameters: [{ type: "Path", name: "id", schema: z.number() }],
    status: 200,
    response: z.instanceof(Blob),
  })
  .addEndpoint({
    alias: "downloadPricingPlanResultCsv",
    method: "get",
    path: "/upload/:id/pricing-plan-result-csv/",
    parameters: [{ type: "Path", name: "id", schema: z.number() }],
    status: 200,
    response: z.instanceof(Blob),
  })
  .addEndpoint({
    alias: "listUploads",
    method: "get",
    path: "/upload/",
    parameters: [
      { type: "Query", name: "upload_type", schema: uploadTypeSchema.optional() },
      { type: "Query", name: "created_by", schema: z.number().optional() },
      { type: "Query", name: "status", schema: z.number().optional() },
      { type: "Query", name: "ordering", schema: z.string().optional() },
      { type: "Query", name: "limit", schema: z.number().optional() },
      { type: "Query", name: "page", schema: z.number().optional() },
    ],
    response: z.object({
      count: z.number(),
      next: z.string().nullable(),
      previous: z.string().nullable(),
      results: z.array(uploadSchema),
    }),
  })
  .build();
